import {
  AdminHeader,
  AdminOptionsDropdown,
  AdminSpinner,
  Button,
  Callout,
  Disclaimer,
  Div,
  ErrorToast,
  H3,
  H5,
  HeaderIconWrapper,
  Img,
  ImportMenuIcon,
  LocationMode,
  P,
  Pill,
  SuccessToast,
  Tab,
  TabsWrapper,
  UserRole,
  getLocationStoreName,
  getMenu,
  getSyncStatus,
  grantNotificationPermission,
  lightTheme,
  publishMenu,
  renderProviderIcon,
  renderProviderName,
  renderStoreName,
  unavailableImage,
  reportError,
  useAuth,
} from '@vgrubs/components';
import {
  GetPublishSyncStatusResponse,
  MenuNamesResponse,
} from 'components/src/types/api/menu.types';
import { MenuV2Response } from 'components/src/types/menu.types';
import { Provider } from 'components/src/types/shared.types';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ImportMenuModal } from './components/ImportMenuModal';
import { ImportLoaderModal } from './components/ImportLoaderModal';
import { useMenuImportAi } from './useMenuImportAi';
import { MenuImportPreview } from './MenuImportPreview';
import { MenuPublishHistory } from './MenuPublishHistory/MenuPublishHistory';
import { MenuPublishHistoryPreview } from './MenuPublishHistoryPreview/MenuPublishHistoryPreview';
import { Availability } from './MenuProviderSections/Availability/Availability';
import { Categories } from './MenuProviderSections/Categories/Categories';
import { Items } from './MenuProviderSections/Items/Items';
import { Modifiers } from './MenuProviderSections/Modifiers/Modifiers';

import { useProviderSyncStatus } from 'components/src/store/useProviderSyncStatus';
import { dateFormat, isAdmin, isPartner, providerName } from '../../helpers';
import useMenuSyncSocket from './MenuSyncSocket';
import { ProviderStatusSyncList } from './components/ProviderStatusSyncList';
import { ImportPosItemsModal } from './components/ImportPosItems';
import { PushOrdersToPosModal } from './components/PushOrdersToPosModal';
import { ClearMenuModal } from './components/ClearMenuModal';
import { getPosItemsImport, importPosItems } from '../../api/menu.api';
import { AIStarsIcon, ChatTrashIcon, ClockSolidIcon } from '../../assets/svg';
import {
  MenuAIImprove,
  MenuAIImproveHandle,
} from './MenuAIImprove/MenuAIImprove';
import { MenuPicker } from './components/MenuPicker';
import { useBrandMenus } from './hooks/useBrandMenus';
import { SelectedMenuContext } from './menuSelection';

export const isMenuProviderDelivery = (provider: Provider) =>
  provider === Provider.delivery;

const buildProviderList = (providers: Provider[]) =>
  providers.map((p) => `<li>${providerName(p)}</li>`).join('');

const buildPublishTooltip = (
  syncStatus: GetPublishSyncStatusResponse | null,
  storeName: MenuNamesResponse | null,
  menuName: string | null
) => {
  const lastPublishedAt = syncStatus?.lastPublishedAt
    ? dateFormat(syncStatus.lastPublishedAt)
    : 'Never';
  const menuLine = menuName ? `<b>Menu: ${menuName}</b><br/>` : '';

  if (!isAdmin) {
    return `${menuLine}<b>Last Published At:  ${lastPublishedAt}</b>`;
  }

  const kitchenhubProviders = buildProviderList([
    Provider.gloriafood,
    Provider.grubhubPos,
    Provider.doordashPos,
    Provider.ubereats,
  ]);
  const foodhubProviders = buildProviderList([
    Provider.doordash,
    Provider.ubereats,
    Provider.gloriafood,
    Provider.grubhub,
    Provider.localForYou,
  ]);

  return `${menuLine}<b>Last Published At:  ${lastPublishedAt}</b><br/><br/>
   Available Kitchenhub providers for menu publish:
   <ul>${kitchenhubProviders}</ul>
   Available Foodhub providers for menu publish:
   <ul>${foodhubProviders}</ul>
   
        ${
          storeName?.locationMode === LocationMode.pos
            ? 'Items that have been selected on our brands and physical location will be applied to the marketplace after 15 minutes.'
            : ''
        }`;
};

type PublishButtonProps = {
  syncStatus: GetPublishSyncStatusResponse | null;
  syncLoading: boolean;
  /** The open menu's name; publish state is per menu. */
  menuName: string | null;
  onPublish: () => void;
  storeName: MenuNamesResponse | null;
};

const PublishButton = ({
  syncStatus,
  syncLoading,
  menuName,
  onPublish,
  storeName,
}: PublishButtonProps) => {
  return (
    <Button
      disabled={!syncStatus?.shouldPublish}
      $selected
      onClick={onPublish}
      $loading={syncLoading}
      $wSM="130px"
      $tooltip={buildPublishTooltip(syncStatus, storeName, menuName)}
    >
      {syncStatus?.shouldPublish ? 'Publish' : 'Published'}
    </Button>
  );
};

export const Menu = () => {
  const navigate = useNavigate();
  const [importMenuModal, setImportMenuModal] = useState(false);
  const [pushOrdersToPosModal, setPushOrdersToPosModal] = useState(false);
  const [menu, setMenu] = useState<MenuV2Response | null>(null);
  const [posItemsImport, setPosItemsImport] = useState<MenuV2Response | null>(
    null
  );
  // const [simpleMenu, setSimpleMenu] = useState<MenuEntity[] | null>(null);
  // Starts true: on entry the names call and the menu list go out first and
  // the menu itself only once the list is in, so without this the page sits
  // empty with no spinner for that first round trip.
  const [loading, setLoading] = useState(true);
  const { locationId, brandId } = useParams() as {
    locationId: string;
    brandId: string;
  };

  // The brand's menus and the one open on screen. Every menu-level request
  // below carries `menuId`; the sections and modals read it from context.
  const brandMenus = useBrandMenus({ locationId, brandId });
  const {
    menuId,
    selectedMenu,
    menus,
    ready: menusReady,
    patchMenu,
  } = brandMenus;

  const [syncStatus, setSyncStatus] =
    useState<GetPublishSyncStatusResponse | null>(null);

  const handleGetSyncStatus = async (silent = false) => {
    if (!silent) setSyncLoading(true);
    try {
      const { data } = await getSyncStatus(locationId, brandId, menuId);
      setSyncStatus(data.data);
    } catch (error) {
      console.error(error);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleChangeShouldPublish = (shouldPublish: boolean) => {
    setSyncStatus((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        shouldPublish,
      };
    });
    if (menuId) patchMenu(menuId, { shouldPublish });
  };

  // Socket: `menu_should_publish` names the menu it is about. Editing
  // availability in one menu flags its linked copies too, so a menu that is
  // not on screen can turn unpublished; the picker's dot has to follow.
  // Without a picked menu (multi-menu off) every event is about this one.
  const handleSocketShouldPublish = (
    eventMenuId: number | null,
    shouldPublish: boolean
  ) => {
    if (menuId === null || eventMenuId === null || eventMenuId === menuId) {
      setSyncStatus((prev) => (prev ? { ...prev, shouldPublish } : null));
    }
    if (eventMenuId !== null) patchMenu(eventMenuId, { shouldPublish });
  };

  useMenuSyncSocket({
    locationId: Number(locationId),
    brandId: Number(brandId),
    menuId,
    setSyncStatus,
    onShouldPublish: handleSocketShouldPublish,
  });

  const [providerToImport, setProviderToImport] = useState<string | null>(null);
  const [urlToScrape, setUrlToScrape] = useState<string | null>(null);
  const [importPreviewMenu, setImportPreviewMenu] =
    useState<MenuV2Response | null>(null);
  const [importSource, setImportSource] = useState<{
    locationId: number;
    brandId: number | null;
  } | null>(null);

  const handleSetMenuToImport = (
    menu: MenuV2Response,
    source: { locationId: number; brandId: number | null }
  ) => {
    setImportPreviewMenu(menu);
    setImportSource(source);
  };
  const [publishHistoryOpen, setPublishHistoryOpen] = useState(false);
  const [publishHistoryPreviewId, setPublishHistoryPreviewId] = useState<
    number | null
  >(null);
  const [aiPreviewOpen, setAiPreviewOpen] = useState(false);
  const aiImproveRef = useRef<MenuAIImproveHandle>(null);
  const [clearMenuModal, setClearMenuModal] = useState(false);

  // Clearing is admin / support only on the backend (403 otherwise), so the
  // action is hidden from every other admin-app role.
  const role = useAuth((state) => state.user?.role);
  const canClearMenu =
    isAdmin && (role === UserRole.admin || role === UserRole.support);

  // Every open of the Improve-with-AI feature starts fresh: delete any lingering
  // draft first (a user may have made changes and neither saved nor cancelled —
  // e.g. refreshed or closed the tab — and those would otherwise stack). Shows a
  // full-screen loader; on a backend error we surface the message and keep the
  // modal closed so the user can retry.
  const handleOpenAiImprove = async () => {
    const ok = await aiImproveRef.current?.discardDraft();
    if (ok !== false) setAiPreviewOpen(true);
  };

  const [syncLoading, setSyncLoading] = useState(false);
  const [menuTab, setMenuTab] = useState(1);
  const [isNotConnected, setIsNotConnected] = useState<string | null>(null);

  // Switching menus quickly fires overlapping requests; only the newest one
  // may land, or a slow answer for the previous menu would overwrite it.
  const menuRequestRef = useRef(0);

  async function handleGetMenu() {
    const request = ++menuRequestRef.current;
    setMenu(null);
    try {
      setLoading(true);
      const { data } = await getMenu(locationId, brandId, menuId);
      if (request === menuRequestRef.current) setMenu(data.data);
    } catch (error) {
      if (request === menuRequestRef.current) reportError(error);
    } finally {
      if (request === menuRequestRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    getStoreName();
    grantNotificationPermission();
    setIsNotConnected(null);
  }, []);

  // The menu content waits for the menu list: until it has settled we do not
  // know which menu to ask for. Re-runs whenever the open menu changes.
  useEffect(() => {
    if (!menusReady) return;
    handleGetMenu();
    handleGetSyncStatus(true);
  }, [menusReady, menuId]);

  const [storeName, setStoreName] = useState<MenuNamesResponse | null>(null);

  // The header (store name, POS pill and callout, POS-only actions) comes
  // from this call, so the spinner holds until it is in as well.
  const [namesLoading, setNamesLoading] = useState(true);

  async function getStoreName() {
    try {
      const { data } = await getLocationStoreName(locationId, brandId);
      setStoreName(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setNamesLoading(false);
    }
  }

  // --- keep global fields via store (not scoped) ---
  const { initializedProviderConnection, setInitializedProviderConnection } =
    useProviderSyncStatus();

  // --- scoped statuses for (locationId, brandId) + scoped setter wrapper ---
  const statuses = useProviderSyncStatus(
    (st) =>
      st.statusesByScope?.find(
        (e) =>
          e.locationId === Number(locationId) && e.brandId === Number(brandId)
      )?.statuses ?? []
  );

  const setStatuses = (
    value: import('components/src/types/api/location.types').ProviderAndProviderStatusResponse
  ) =>
    useProviderSyncStatus
      .getState()
      .setStatusesFor(
        { locationId: Number(locationId), brandId: Number(brandId) },
        value
      );

  const store = storeName ? renderStoreName(storeName) : '';

  // "Import Menu with AI": upload a document (+ optional prompt), poll for the
  // parsed result, then feed it into the same import preview editor.
  const { startAiImport, aiLoading, setAiLoading } = useMenuImportAi({
    locationId,
    brandId,
    allowedToEditMenuPrice: storeName?.allowedToEditMenuPrice,
    onReady: (importedMenu) => setImportPreviewMenu(importedMenu),
  });

  const handlePublish = async (enablePushOrdersToPos?: boolean) => {
    try {
      setSyncLoading(true);
      const { data } = await publishMenu(
        locationId,
        brandId,
        enablePushOrdersToPos === undefined
          ? undefined
          : { enablePushOrdersToPos },
        menuId
      );
      setSyncStatus(data.data);
      if (menuId)
        patchMenu(menuId, {
          shouldPublish: data.data.shouldPublish,
          lastPublishedAt:
            data.data.lastPublishedAt ?? new Date().toISOString(),
        });

      if (enablePushOrdersToPos !== undefined) {
        setStoreName((prev) =>
          prev ? { ...prev, pushOrdersToPos: enablePushOrdersToPos } : prev
        );
      }

      if (initializedProviderConnection) {
        setStatuses(
          statuses.map((item) => {
            if (item.provider === initializedProviderConnection) {
              return { ...item, haveMenuSet: true };
            }
            return item;
          })
        );
      }

      setInitializedProviderConnection(null);
    } catch (error) {
      reportError(error);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleImport = () => {
    if (storeName?.locationMode === LocationMode.pos) {
      handleGetPosItemsImport();
    } else {
      setImportMenuModal(true);
    }
  };

  const handleGetPosItemsImport = async () => {
    try {
      setLoading(true);
      const { data } = await getPosItemsImport(locationId, brandId);
      setPosItemsImport(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleImportPosItems = async (categoryIds: number[]) => {
    const existingPosCategoryIds = new Set<number>();

    menu?.categories.forEach((category) => {
      if (category?.posId != null) {
        existingPosCategoryIds.add(category.posId);
      }
    });

    const filteredCategoryIds = categoryIds.filter(
      (id) => !existingPosCategoryIds.has(id)
    );

    if (filteredCategoryIds.length === 0) {
      ErrorToast(
        `Please select at least one ${
          menu?.categories[0] ? 'new (not imported yet)' : ''
        } category to import.`
      );
      return;
    }

    try {
      setLoading(true);
      const { data } = await importPosItems(
        locationId,
        brandId,
        { categoryIds: filteredCategoryIds },
        menuId
      );
      SuccessToast(data.message);
      setPosItemsImport(null);
      setMenu(data.data);
      handleChangeShouldPublish(true);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  const isPos = storeName?.locationMode === LocationMode.pos;
  const isBrand = brandId !== '0';
  const shouldConfirmPushOrdersToPos =
    isAdmin && isPos && isBrand && !storeName?.pushOrdersToPos;

  const handlePublishClick = () => {
    if (shouldConfirmPushOrdersToPos) {
      setPushOrdersToPosModal(true);
    } else {
      handlePublish();
    }
  };

  const actions = [
    {
      label: 'Import Menu',
      onClick: handleImport,
      Icon: ImportMenuIcon,
      show: isAdmin,
    },
    {
      label: 'Import POS Items',
      onClick: handleGetPosItemsImport,
      Icon: ImportMenuIcon,
      show: isPartner && isPos,
    },
    {
      label: 'Publish History',
      onClick: () => setPublishHistoryOpen(true),
      Icon: ({ color = 'white' }: { color?: string }) => (
        <ClockSolidIcon color={color} />
      ),
      show: isAdmin,
    },
    {
      label: 'Improve Menu with AI',
      onClick: handleOpenAiImprove,
      // AIStarsIcon has a fixed gradient and ignores `color`; wrap it so the
      // action's Icon type stays consistent with the others.
      Icon: (_props: { color?: string }) => <AIStarsIcon />,
      show: isAdmin && !isPos,
    },
    {
      // Allowed on POS locations too: clearing is the reset before a fresh
      // POS import, so the "no manual edits on POS" rule does not apply.
      label: 'Clear Menu',
      onClick: () => setClearMenuModal(true),
      Icon: ({ color }: { color?: string }) => <ChatTrashIcon color={color} />,
      show: canClearMenu,
    },
  ];

  const Pills = () => {
    return (
      <Div $flex $alignCenter $gap="16px" $gapSM="8px">
        {isPos && (
          <Pill $background={lightTheme.primary}>
            <Disclaimer $m="0" $color={lightTheme.white} $uppercase>
              {storeName?.locationMode}
            </Disclaimer>
          </Pill>
        )}

        {isBrand && storeName?.pushOrdersToPos && (
          <Pill $background={lightTheme.primary}>
            <Disclaimer $m="0" $color={lightTheme.white}>
              Push to POS Enabled
            </Disclaimer>
          </Pill>
        )}
      </Div>
    );
  };

  return (
    <SelectedMenuContext.Provider value={{ menuId, menu: selectedMenu, menus }}>
      {(loading || namesLoading) && <AdminSpinner />}
      <AdminHeader title="Menu" subtitle={store}>
        <Div $flex $alignCenter $gap="16px" $gapSM="8px">
          <Div $desktop>
            <Pills />
          </Div>

          {actions.map(
            (action) =>
              action.show && (
                <Div $desktop key={action.label}>
                  <HeaderIconWrapper onClick={action.onClick}>
                    <action.Icon color={lightTheme.primary} />
                  </HeaderIconWrapper>
                </Div>
              )
          )}

          <Div>
            <PublishButton
              syncStatus={syncStatus}
              syncLoading={syncLoading}
              storeName={storeName}
              menuName={selectedMenu?.name ?? null}
              onPublish={handlePublishClick}
            />
          </Div>

          <Div $mobile>
            <AdminOptionsDropdown actions={actions} />
          </Div>
        </Div>
      </AdminHeader>

      <Div $mobile $mt="20">
        <Pills />
      </Div>

      <Div $p="0 0 100px 0">
        <>
          {isPos && isAdmin && (
            <Div $mt="16">
              <Callout tone="info">
                {storeName?.allowedToEditMenuPrice
                  ? 'The POS is the source of truth for this menu. Items and availability come from it, so change them in the POS and import again. Prices are the exception: a different one can be set per marketplace here.'
                  : 'The POS is the source of truth for this menu. Items, prices and availability all come from it. Change them in the POS and import again, nothing is edited here.'}
              </Callout>
            </Div>
          )}

          <MenuPicker
            brandMenus={brandMenus}
            locationId={locationId}
            brandId={brandId}
            isPos={isPos}
          />

          <TabsWrapper>
            <Tab selected={menuTab === 1} onClick={() => setMenuTab(1)}>
              Items
            </Tab>
            <Tab selected={menuTab === 2} onClick={() => setMenuTab(2)}>
              Modifiers
            </Tab>
            <Tab selected={menuTab === 3} onClick={() => setMenuTab(3)}>
              Categories
            </Tab>
            <Tab selected={menuTab === 4} onClick={() => setMenuTab(4)}>
              Availability
            </Tab>
          </TabsWrapper>

          <ProviderStatusSyncList
            syncStatus={syncStatus}
            setSyncStatus={setSyncStatus}
            locationId={locationId}
            brandId={brandId}
          />

          {!isNotConnected && (
            <>
              {menuTab === 1 && (
                <Items
                  allowedToEditMenuPrice={
                    storeName?.allowedToEditMenuPrice || false
                  }
                  menu={menu}
                  setMenu={setMenu}
                  setLoading={setLoading}
                  handleChangeShouldPublish={handleChangeShouldPublish}
                  isPos={isPos}
                  timeZone={storeName?.location?.timeZone}
                />
              )}
              {menuTab === 2 && (
                <Modifiers
                  menu={menu}
                  setMenu={setMenu}
                  setLoading={setLoading}
                  handleChangeShouldPublish={handleChangeShouldPublish}
                  isPos={isPos}
                  allowedToEditMenuPrice={
                    storeName?.allowedToEditMenuPrice || false
                  }
                />
              )}
              {menuTab === 3 && (
                <Categories
                  menu={menu}
                  setMenu={setMenu}
                  setLoading={setLoading}
                  handleChangeShouldPublish={handleChangeShouldPublish}
                  isPos={isPos}
                />
              )}
              {menuTab === 4 && (
                <Availability
                  menu={menu}
                  setMenu={setMenu}
                  setLoading={setLoading}
                  handleChangeShouldPublish={handleChangeShouldPublish}
                  isPos={isPos}
                />
              )}
            </>
          )}
        </>
      </Div>

      {isNotConnected && (
        <Div $flex $column $alignCenter>
          <Img alt="" $width="180px" src={unavailableImage} $mb="24" />
          <H3 $mb="24" $center>
            Unavailable
          </H3>
          <P $mb="32" $center $maxWidth="350px">
            {isNotConnected}
          </P>
        </Div>
      )}

      {isAdmin && (
        <ImportMenuModal
          importMenuModal={importMenuModal}
          closeModal={() => {
            setImportMenuModal(false);
          }}
          setProviderToImport={setProviderToImport}
          setUrlToScrape={setUrlToScrape}
          setMenuToImport={handleSetMenuToImport}
          setLoading={setLoading}
          startAiImport={startAiImport}
        />
      )}

      {isAdmin && (
        <ImportLoaderModal loading={aiLoading} setLoading={setAiLoading} glow />
      )}

      {isAdmin && (
        <MenuImportPreview
          scrappingUrl={urlToScrape}
          setScrappingUrl={setUrlToScrape}
          importProvider={providerToImport}
          storeName={store}
          closeModal={() => {
            setProviderToImport(null);
            setImportPreviewMenu(null);
            setImportSource(null);
          }}
          setMenuMain={setMenu}
          menu={importPreviewMenu}
          setMenu={setImportPreviewMenu}
          importSource={importSource}
          handleGetSyncStatus={handleGetSyncStatus}
          handleChangeShouldPublish={handleChangeShouldPublish}
        />
      )}

      {/* <AreYouSureLeave
        modalIsOpen={modalIsOpen}
        handleClick={handleClick}
      /> */}

      {isPos && (
        <ImportPosItemsModal
          isOpen={!!posItemsImport}
          onClose={() => setPosItemsImport(null)}
          posItemsImport={posItemsImport}
          handleImportPosItems={handleImportPosItems}
          menu={menu}
        />
      )}

      {shouldConfirmPushOrdersToPos && (
        <PushOrdersToPosModal
          isOpen={pushOrdersToPosModal}
          closeModal={() => setPushOrdersToPosModal(false)}
          onConfirm={(enablePushOrdersToPos) => {
            setPushOrdersToPosModal(false);
            handlePublish(enablePushOrdersToPos);
          }}
          storeName={store}
        />
      )}

      <MenuPublishHistory
        isOpen={publishHistoryOpen}
        closeModal={() => setPublishHistoryOpen(false)}
        onPreview={(item) => {
          setPublishHistoryOpen(false);
          setPublishHistoryPreviewId(item.id);
        }}
      />

      <MenuPublishHistoryPreview
        historyId={publishHistoryPreviewId}
        closeModal={() => setPublishHistoryPreviewId(null)}
        setMenuMain={setMenu}
        handleChangeShouldPublish={handleChangeShouldPublish}
        isPos={isPos}
      />

      {canClearMenu && (
        <ClearMenuModal
          isOpen={clearMenuModal}
          closeModal={() => setClearMenuModal(false)}
          locationId={locationId}
          brandId={brandId}
          storeName={storeName}
          onCleared={(cleared) => {
            setMenu(cleared);
            // Providers still serve the old menu until the next publish.
            handleChangeShouldPublish(true);
          }}
        />
      )}

      {isAdmin && !isPos && (
        <MenuAIImprove
          ref={aiImproveRef}
          open={aiPreviewOpen}
          currentMenu={menu}
          onClose={() => setAiPreviewOpen(false)}
          onSaved={() => {
            handleGetMenu();
            handleChangeShouldPublish(true);
            setAiPreviewOpen(false);
          }}
        />
      )}
    </SelectedMenuContext.Provider>
  );
};

export const MenuItemProvider = ({ provider }: { provider: Provider }) => {
  return (
    <Div $box $p="10px 24px" $flex $alignCenter $between $mb="8">
      <Div $flex $alignCenter $gap="12px">
        <Img alt="" src={renderProviderIcon(provider)} $width="35px" />
        <H5 $m="0">{renderProviderName(provider)}</H5>
      </Div>
    </Div>
  );
};
